/**
 * UNIFIED CONTENT FORGE
 * Merges Quick Generate (5 credits) + Deep Forge / Hermes (25-35 credits)
 * into a single tool with mode toggle and configuration screen.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useWorkflow } from '../../contexts/WorkflowContext';
import { useIntelligence } from '../../contexts/IntelligenceContext';
import { RiverButton, RiverBanner } from '../ui/RiverFlow';
import {
  Zap, Flame, ArrowRight, ChevronDown, Loader2,
  Copy, Check, Download, RefreshCw, Sparkles, Clock, FileText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CONTENT_ANGLES = [
  { id: 'thought_leadership', label: 'Thought Leadership' },
  { id: 'how_to_guide', label: 'How-To Guide' },
  { id: 'news_reaction', label: 'Industry News Reaction' },
  { id: 'counter_competitor', label: 'Counter-Competitor Piece' },
  { id: 'case_study', label: 'Case Study' },
  { id: 'hot_take', label: 'Opinion / Hot Take' },
];

const TONES = [
  { id: 'professional', label: 'Professional' },
  { id: 'casual', label: 'Casual' },
  { id: 'friendly', label: 'Friendly' },
  { id: 'formal', label: 'Formal' },
  { id: 'creative', label: 'Creative' },
];

const FORGE_STEPS = [
  { num: 1, emoji: '🔍', name: 'Trend Scan', desc: 'Research competitors writing about ' },
  { num: 2, emoji: '📊', name: 'Top Content Analysis', desc: 'Analyze top-performing content on ' },
  { num: 3, emoji: '📋', name: 'Content Brief', desc: 'Build a research-backed brief for ' },
  { num: 4, emoji: '✍️', name: 'Draft Generation', desc: 'Write 1500+ word article on ' },
  { num: 5, emoji: '🎯', name: 'SEO Optimization', desc: 'Optimize for keyword ' },
  { num: 6, emoji: '📱', name: 'Social Variations', desc: 'Create platform-optimized versions of ' },
  { num: 7, emoji: '#️⃣', name: 'Hashtag Engine', desc: 'Generate trending hashtags for ' },
  { num: 8, emoji: '📧', name: 'Email Newsletter', desc: 'Convert into email format for ' },
  { num: 9, emoji: '🏆', name: 'A/B Headlines', desc: 'Generate 5 headline variations for ' },
  { num: 10, emoji: '📈', name: 'Performance Prediction', desc: 'Predict engagement metrics for ' },
  { num: 11, emoji: '🎨', name: 'Visual Suggestions', desc: 'Recommend images and graphics for ' },
  { num: 12, emoji: '📦', name: 'Package Assembly', desc: 'Bundle everything into deliverable for ' },
];

const CONTENT_TYPES = [
  { value: 'blog_post', label: 'Blog Post', icon: '📝' },
  { value: 'social_media', label: 'Social Media Post', icon: '📱' },
  { value: 'email_campaign', label: 'Email Campaign', icon: '✉️' },
  { value: 'marketing_copy', label: 'Marketing / Ad Copy', icon: '📣' },
  { value: 'product_description', label: 'Product Description', icon: '🏷️' },
];

// ─── Mode Toggle ───
const ModeToggle = ({ mode, setMode }) => (
  <div className="flex rounded-xl overflow-hidden border-2 border-gray-700 bg-gray-900/50" data-testid="mode-toggle">
    <button
      onClick={() => setMode('quick')}
      className={`flex-1 px-6 py-4 transition-all ${
        mode === 'quick'
          ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white'
          : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
      }`}
      data-testid="mode-quick"
    >
      <div className="flex items-center justify-center gap-2">
        <Zap className="w-5 h-5" />
        <span className="font-bold">Quick Generate</span>
      </div>
      <p className="text-xs mt-1 opacity-80">~30 seconds • 5 credits • Social posts & drafts</p>
    </button>
    <button
      onClick={() => setMode('deep')}
      className={`flex-1 px-6 py-4 transition-all ${
        mode === 'deep'
          ? 'bg-gradient-to-r from-orange-600 to-amber-600 text-white'
          : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
      }`}
      data-testid="mode-deep"
    >
      <div className="flex items-center justify-center gap-2">
        <Flame className="w-5 h-5" />
        <span className="font-bold">Deep Forge</span>
      </div>
      <p className="text-xs mt-1 opacity-80">~3 minutes • 25-35 credits • Full research-backed content</p>
    </button>
  </div>
);

// ─── Section Content Renderer ───
// Handles every shape the LLM might emit for a section:
//  - "plain string"
//  - { text: "..." }
//  - { title, text }
//  - { heading, content }                       ← Main Content array items
//  - { subheading_1: { title, text }, ... }     ← numbered subheadings
//  - [ {heading, content}, {heading, content} ] ← Main Content as array
//  - [ "string", "string" ]                     ← bullet-style array
// Never renders raw JSON — always unwraps one level deeper before falling back.
const SectionContent = ({ value }) => {
  // Plain string
  if (typeof value === 'string') {
    return <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">{value}</p>;
  }

  // Array — render each item recursively. This is the most common Main Content shape.
  if (Array.isArray(value)) {
    return (
      <div className="space-y-4">
        {value.map((item, i) => (
          <SectionContent key={i} value={item} />
        ))}
      </div>
    );
  }

  if (!value || typeof value !== 'object') {
    return <p className="text-gray-300 leading-relaxed">{String(value)}</p>;
  }

  // {heading, content} — the exact shape user reported
  if (value.heading && (typeof value.content === 'string' || typeof value.content === 'object')) {
    return (
      <div className="mb-3">
        <h4 className="text-sm font-bold text-gray-200 mb-1.5">{value.heading}</h4>
        {typeof value.content === 'string'
          ? <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">{value.content}</p>
          : <SectionContent value={value.content} />}
      </div>
    );
  }

  // {title, text}
  if (value.title && value.text) {
    return (
      <div className="ml-2 mb-3">
        <h4 className="text-sm font-bold text-gray-200 mb-1">{value.title}</h4>
        <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">{value.text}</p>
      </div>
    );
  }

  // Just {text} or just {content} — flatten to a paragraph
  if (typeof value.text === 'string') {
    return <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">{value.text}</p>;
  }
  if (typeof value.content === 'string') {
    return <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">{value.content}</p>;
  }

  // Object with subheading_N / point_N / section_N keys → iterate each as a heading+text block
  const entries = Object.entries(value);
  const hasSubheadings = entries.some(([k]) =>
    k.startsWith('subheading') || k.startsWith('point') || k.startsWith('section') || k.startsWith('item')
  );
  if (hasSubheadings || entries.every(([, v]) => v && typeof v === 'object')) {
    return (
      <div className="space-y-4 ml-2">
        {entries.map(([subKey, subVal], j) => {
          if (typeof subVal === 'string') {
            return <p key={j} className="text-gray-300 leading-relaxed">{subVal}</p>;
          }
          // Recurse — handles {title, text}, {heading, content}, nested arrays, etc.
          return <SectionContent key={j} value={subVal} />;
        })}
      </div>
    );
  }

  // Final fallback: render each key as a labeled paragraph (humanized key name).
  // We NEVER show JSON.stringify output to the user.
  return (
    <div className="space-y-2 ml-2">
      {entries.map(([subKey, subVal], j) => (
        <div key={j}>
          <span className="text-xs uppercase tracking-wider text-gray-500 mr-2">
            {String(subKey).replace(/_/g, ' ')}:
          </span>
          {typeof subVal === 'string' ? (
            <span className="text-gray-300 leading-relaxed">{subVal}</span>
          ) : (
            <SectionContent value={subVal} />
          )}
        </div>
      ))}
    </div>
  );
};

// ─── Formatted Content Renderer ───
const FormattedContent = ({ data }) => {
  let content = data.content;
  let parsed = null;

  // Strip markdown code fences if present (```json ... ```)
  if (typeof content === 'string') {
    let cleaned = content.trim();
    if (cleaned.startsWith('```')) {
      cleaned = cleaned.replace(/^```(?:json)?\s*\n?/, '').replace(/\n?```\s*$/, '').trim();
    }
    try {
      parsed = JSON.parse(cleaned);
    } catch {
      // Not JSON — treat as plain text/markdown
    }
  } else if (typeof content === 'object' && content !== null) {
    parsed = content;
  }

  // Unwrap common wrapper keys
  if (parsed?.blog_post) parsed = parsed.blog_post;
  if (parsed?.article) parsed = parsed.article;
  if (parsed?.post) parsed = parsed.post;

  // Case 1: Parsed object with content as nested object (section keys → text)
  if (parsed && typeof parsed.content === 'object' && !Array.isArray(parsed.content)) {
    const sections = Object.entries(parsed.content);
    return (
      <>
        {parsed.title && <h2 className="text-xl font-bold text-white mb-4">{parsed.title}</h2>}
        {sections.map(([key, value], i) => (
          <div key={i} className="mb-6">
            <h3 className="text-base font-semibold text-purple-300 mb-2">{key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</h3>
            <SectionContent value={value} />
          </div>
        ))}
      </>
    );
  }

  // Case 2: Parsed object with content as array of sections
  if (parsed && Array.isArray(parsed.content)) {
    return (
      <>
        {parsed.title && <h2 className="text-xl font-bold text-white mb-4">{parsed.title}</h2>}
        {parsed.content.map((section, i) => (
          <div key={i} className="mb-5">
            {(section.section || section.heading || section.title) && (
              <h3 className="text-base font-semibold text-purple-300 mb-1.5">{section.section || section.heading || section.title}</h3>
            )}
            <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">{section.text || section.content || section.body || (typeof section === 'string' ? section : '')}</p>
          </div>
        ))}
      </>
    );
  }

  // Case 3: Parsed with sections array directly
  if (parsed && Array.isArray(parsed.sections)) {
    return (
      <>
        {parsed.title && <h2 className="text-xl font-bold text-white mb-4">{parsed.title}</h2>}
        {parsed.sections.map((section, i) => (
          <div key={i} className="mb-5">
            {(section.section || section.heading || section.title) && (
              <h3 className="text-base font-semibold text-purple-300 mb-1.5">{section.section || section.heading || section.title}</h3>
            )}
            <p className="text-gray-300 leading-relaxed whitespace-pre-wrap">{section.text || section.content || section.body || ''}</p>
          </div>
        ))}
      </>
    );
  }

  // Case 4: Parsed object with just a title and string content
  if (parsed && typeof parsed.content === 'string') {
    content = parsed.content;
    // Fall through to plain text rendering below
  }

  // Case 5: Plain text / markdown string
  if (typeof content === 'string' && content) {
    // Strip code fences again in case
    let text = content.trim();
    if (text.startsWith('```')) {
      text = text.replace(/^```(?:json)?\s*\n?/, '').replace(/\n?```\s*$/, '').trim();
    }
    const lines = text.split('\n');
    return (
      <>
        {parsed?.title && <h2 className="text-xl font-bold text-white mb-4">{parsed.title}</h2>}
        {lines.map((line, i) => {
          if (!line.trim()) return <br key={i} />;
          if (line.startsWith('### ')) return <h4 key={i} className="text-sm font-bold text-gray-200 mt-3 mb-1">{line.slice(4)}</h4>;
          if (line.startsWith('## ')) return <h3 key={i} className="text-base font-semibold text-purple-300 mt-4 mb-1">{line.slice(3)}</h3>;
          if (line.startsWith('# ')) return <h2 key={i} className="text-lg font-bold text-white mt-4 mb-2">{line.slice(2)}</h2>;
          if (line.startsWith('- ') || line.startsWith('* ')) return <li key={i} className="ml-4 text-gray-300 mb-1">{line.slice(2)}</li>;
          if (/^\d+\.\s/.test(line)) return <li key={i} className="ml-4 text-gray-300 list-decimal mb-1">{line.replace(/^\d+\.\s/, '')}</li>;
          const formatted = line.replace(/\*\*(.+?)\*\*/g, '<strong class="text-white font-semibold">$1</strong>');
          if (formatted !== line) return <p key={i} className="text-gray-300 mb-2 leading-relaxed" dangerouslySetInnerHTML={{ __html: formatted }} />;
          return <p key={i} className="text-gray-300 mb-2 leading-relaxed">{line}</p>;
        })}
      </>
    );
  }

  // Fallback — recursively render the structure rather than dumping JSON
  return <SectionContent value={data.content || data} />;
};

// ─── Quick Generate Form ───
const QuickGenerateForm = ({ onGenerate, onGenerateVariants, loading, generatedContent, variantsResult, startRiver, error, variantMode, setVariantMode, seoOptimize, setSeoOptimize, prefillTopic }) => {
  const [formData, setFormData] = useState({
    content_type: 'blog_post',
    title: '',
    description: '',
    target_audience: '',
    tone: 'professional',
    word_count: 500,
    keywords: [],
    language: 'en'
  });
  const [keywordInput, setKeywordInput] = useState('');
  const [selectedVariantIdx, setSelectedVariantIdx] = useState(0);
  const { workflow } = useWorkflow();

  useEffect(() => {
    if (workflow.active && (workflow.targetTool === 'generate' || workflow.targetTool === 'hermes-forge')) {
      setFormData(prev => ({
        ...prev,
        title: workflow.topic || prev.title,
        description: workflow.topic || prev.description,
        content_type: workflow.contentType || prev.content_type,
        keywords: workflow.keywords ? workflow.keywords.split(',').map(k => k.trim()).filter(Boolean) : prev.keywords,
      }));
    }
  }, [workflow]);

  // Hydrate from sessionStorage prefill (digest email "Forge content on this →")
  useEffect(() => {
    if (prefillTopic) {
      setFormData(prev => ({
        ...prev,
        title: prefillTopic,
        description: prefillTopic,
      }));
    }
  }, [prefillTopic]);

  useEffect(() => {
    // Reset variant selection when new variants arrive
    if (variantsResult?.variants?.length) setSelectedVariantIdx(0);
  }, [variantsResult]);

  const addKeyword = () => {
    if (keywordInput.trim() && !formData.keywords.includes(keywordInput.trim())) {
      setFormData({ ...formData, keywords: [...formData.keywords, keywordInput.trim()] });
      setKeywordInput('');
    }
  };

  const handleGenerate = () => {
    const payload = { ...formData, seo_optimize: seoOptimize };
    if (variantMode === 'three') onGenerateVariants(payload);
    else onGenerate(payload);
  };

  return (
    <div className="space-y-5" data-testid="quick-generate-form">
      {/* Content Type */}
      <div>
        <label className="text-sm font-semibold text-gray-300 mb-2 block">Content Type</label>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {CONTENT_TYPES.map(t => (
            <button
              key={t.value}
              onClick={() => setFormData({ ...formData, content_type: t.value })}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                formData.content_type === t.value
                  ? 'bg-blue-500/20 border-blue-500/50 text-blue-300 border'
                  : 'bg-gray-800/50 border border-gray-700/50 text-gray-400 hover:text-white hover:border-gray-600'
              }`}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Title / Topic */}
      <div>
        <label className="text-sm font-semibold text-gray-300 mb-2 block">Topic / Title</label>
        <input
          type="text"
          value={formData.title}
          onChange={e => setFormData({ ...formData, title: e.target.value })}
          placeholder="e.g., AI Marketing Strategy for SaaS Startups"
          className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-blue-500/50 focus:outline-none"
          data-testid="quick-topic-input"
        />
      </div>

      {/* Description */}
      <div>
        <label className="text-sm font-semibold text-gray-300 mb-2 block">Description / Brief</label>
        <textarea
          value={formData.description}
          onChange={e => setFormData({ ...formData, description: e.target.value })}
          placeholder="What should this content cover? Key points, angle, target audience..."
          rows={3}
          className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-blue-500/50 focus:outline-none resize-none"
          data-testid="quick-description-input"
        />
      </div>

      {/* Tone + Word Count */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="text-sm font-semibold text-gray-300 mb-2 block">Tone</label>
          <select
            value={formData.tone}
            onChange={e => setFormData({ ...formData, tone: e.target.value })}
            className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-blue-500/50 focus:outline-none"
          >
            {TONES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-sm font-semibold text-gray-300 mb-2 block">Word Count</label>
          <select
            value={formData.word_count}
            onChange={e => setFormData({ ...formData, word_count: parseInt(e.target.value) })}
            className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-blue-500/50 focus:outline-none"
          >
            <option value={300}>~300 (Short)</option>
            <option value={500}>~500 (Medium)</option>
            <option value={800}>~800 (Long)</option>
            <option value={1200}>~1200 (Very Long)</option>
          </select>
        </div>
      </div>

      {/* Keywords */}
      <div>
        <label className="text-sm font-semibold text-gray-300 mb-2 block">Keywords</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={keywordInput}
            onChange={e => setKeywordInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addKeyword(); } }}
            placeholder="Add keyword and press Enter"
            className="flex-1 bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-2.5 text-white placeholder-gray-500 focus:border-blue-500/50 focus:outline-none text-sm"
          />
          <button onClick={addKeyword} className="px-4 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium">Add</button>
        </div>
        {formData.keywords.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {formData.keywords.map((k, idx) => (
              <span key={`${k}-${idx}`} className="px-2.5 py-1 bg-blue-500/20 border border-blue-500/30 rounded-full text-xs text-blue-300 flex items-center gap-1.5">
                {k}
                <button onClick={() => setFormData({ ...formData, keywords: formData.keywords.filter((_, xIdx) => xIdx !== idx) })} className="text-blue-400 hover:text-white">×</button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* SEO Toggle + Variant Mode Toggle */}
      <div className="grid md:grid-cols-2 gap-4">
        {/* SEO Baked-In Toggle */}
        <div className="rounded-xl border border-gray-700 bg-gray-900/40 p-4" data-testid="seo-toggle-section">
          <div className="flex items-center justify-between gap-3 mb-1">
            <div className="flex items-center gap-2">
              <span>🔍</span>
              <span className="text-sm font-semibold text-gray-200">SEO Baked-In</span>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={seoOptimize}
                onChange={(e) => setSeoOptimize(e.target.checked)}
                className="sr-only peer"
                data-testid="seo-optimize-toggle"
              />
              <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-600"></div>
            </label>
          </div>
          <p className="text-xs text-gray-500">
            Auto-generates meta title + description, internal link suggestions, and readability optimization.
          </p>
        </div>

        {/* Variant Mode Toggle */}
        <div className="rounded-xl border border-gray-700 bg-gray-900/40 p-4" data-testid="variant-mode-section">
          <div className="flex items-center justify-between gap-3 mb-2">
            <div className="flex items-center gap-2">
              <span>🎭</span>
              <span className="text-sm font-semibold text-gray-200">Generation Mode</span>
            </div>
          </div>
          <div className="flex rounded-lg overflow-hidden border border-gray-700">
            <button
              onClick={() => setVariantMode('single')}
              className={`flex-1 px-2 py-1.5 text-xs transition-all ${variantMode === 'single' ? 'bg-blue-600 text-white' : 'bg-gray-800/40 text-gray-400 hover:text-white'}`}
              data-testid="variant-mode-single"
            >
              Single (5)
            </button>
            <button
              onClick={() => setVariantMode('three')}
              className={`flex-1 px-2 py-1.5 text-xs transition-all ${variantMode === 'three' ? 'bg-purple-600 text-white' : 'bg-gray-800/40 text-gray-400 hover:text-white'}`}
              data-testid="variant-mode-three"
            >
              3 Variants (15)
            </button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {variantMode === 'three'
              ? 'Data-Driven, Story-Led, and Contrarian takes — compare angles side-by-side.'
              : 'Best for quick drafts. Use 3 variants when you want to pick the strongest angle.'}
          </p>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm" data-testid="quick-generate-error">
          {error}
        </div>
      )}

      {/* Generate Button */}
      <button
        onClick={handleGenerate}
        disabled={loading || !formData.title.trim()}
        className={`w-full py-4 rounded-xl font-bold text-lg transition-all text-white disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-3 ${
          variantMode === 'three'
            ? 'bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-500 hover:to-blue-500'
            : 'bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-500 hover:to-cyan-500'
        }`}
        data-testid="quick-generate-btn"
      >
        {loading ? (
          <><Loader2 className="w-5 h-5 animate-spin" /> Generating...</>
        ) : variantMode === 'three' ? (
          <><Sparkles className="w-5 h-5" /> Generate 3 Variants (15 credits)</>
        ) : (
          <><Zap className="w-5 h-5" /> Quick Generate (5 credits)</>
        )}
      </button>

      {/* 3-Variants Output */}
      {variantMode === 'three' && variantsResult?.variants?.length > 0 && (
        <div className="mt-6 rounded-xl border border-purple-500/30 bg-gray-900/50 overflow-hidden" data-testid="variants-output">
          <div className="px-5 py-3 border-b border-purple-500/20 bg-gradient-to-r from-purple-500/10 to-blue-500/10 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-purple-400" />
              <h3 className="font-bold text-white">3 Variants — Pick Your Angle</h3>
            </div>
            <span className="text-xs text-gray-400">{variantsResult.credits_used} credits used</span>
          </div>
          <div className="px-5 pt-4">
            <div className="grid grid-cols-3 gap-2">
              {variantsResult.variants.map((v, i) => (
                <button
                  key={v.id || v.type || i}
                  onClick={() => setSelectedVariantIdx(i)}
                  className={`px-3 py-3 rounded-lg border-2 text-left transition-all ${
                    selectedVariantIdx === i
                      ? 'border-purple-400 bg-purple-500/20 text-white'
                      : 'border-gray-700 bg-gray-800/40 text-gray-400 hover:border-gray-600'
                  }`}
                  data-testid={`variant-tab-${i}`}
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    <span>{v.emoji || v.variant_emoji}</span>
                    <span className="font-semibold text-sm">{v.label || v.variant_label}</span>
                  </div>
                  {v.best_for && (
                    <div className="text-[10px] uppercase tracking-wider text-purple-300/80 truncate">
                      Best for: {v.best_for}
                    </div>
                  )}
                  <div className="text-xs opacity-75 mt-0.5">{v.word_count} words</div>
                </button>
              ))}
            </div>
          </div>
          <div className="px-5 py-4 text-sm text-gray-300 leading-relaxed max-h-[500px] overflow-y-auto" data-testid="variant-active-content">
            <FormattedContent data={variantsResult.variants[selectedVariantIdx]} />
          </div>
          <div className="px-5 py-3 border-t border-gray-700/50 flex flex-wrap gap-3">
            <RiverButton label="Distribute This Variant" icon="☀️" onClick={() => startRiver({ topic: variantsResult.variants[selectedVariantIdx]?.title, content: variantsResult.variants[selectedVariantIdx]?.raw_content || variantsResult.variants[selectedVariantIdx]?.content, targetTool: 'apollo-chamber', sourceTool: 'generate' })} className="!text-xs !py-2" />
            <RiverButton label="Multiply This Variant" icon="✨" onClick={() => startRiver({ topic: variantsResult.variants[selectedVariantIdx]?.title, content: variantsResult.variants[selectedVariantIdx]?.raw_content || variantsResult.variants[selectedVariantIdx]?.content, targetTool: 'multiply', sourceTool: 'generate' })} className="!text-xs !py-2" />
            <RiverButton label="SEO Optimize" icon="🔍" onClick={() => startRiver({ topic: variantsResult.variants[selectedVariantIdx]?.title, content: variantsResult.variants[selectedVariantIdx]?.raw_content || variantsResult.variants[selectedVariantIdx]?.content, targetTool: 'seo', sourceTool: 'generate' })} className="!text-xs !py-2" />
          </div>
        </div>
      )}

      {/* Single Generate Output */}
      {variantMode === 'single' && generatedContent && (
        <div className="mt-6 rounded-xl border border-gray-700 bg-gray-900/50 overflow-hidden" data-testid="quick-generate-output">
          <div className="px-5 py-3 border-b border-gray-700/50 flex items-center justify-between">
            <h3 className="font-bold text-white">{generatedContent.title || 'Generated Content'}</h3>
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-500">{generatedContent.word_count || '—'} words</span>
              <button
                onClick={() => onGenerate && window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="text-xs text-purple-400 hover:text-purple-300 font-medium"
              >
                Generate Another
              </button>
            </div>
          </div>
          <div className="px-5 py-4 text-sm text-gray-300 leading-relaxed max-h-[500px] overflow-y-auto">
            <FormattedContent data={generatedContent} />
          </div>
          <div className="px-5 py-3 border-t border-gray-700/50 flex flex-wrap gap-3">
            <RiverButton label="Distribute This" icon="☀️" onClick={() => startRiver({ topic: generatedContent.title, content: generatedContent.content, targetTool: 'apollo-chamber', sourceTool: 'generate' })} className="!text-xs !py-2" />
            <RiverButton label="Multiply This" icon="✨" onClick={() => startRiver({ topic: generatedContent.title, content: generatedContent.content, targetTool: 'multiply', sourceTool: 'generate' })} className="!text-xs !py-2" />
            <RiverButton label="SEO Optimize" icon="🔍" onClick={() => startRiver({ topic: generatedContent.title, content: generatedContent.content, targetTool: 'seo', sourceTool: 'generate' })} className="!text-xs !py-2" />
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Deep Forge Configuration ───
const DeepForgeConfig = ({ onStartForge, loading, topic, setTopic, targetKeyword, setTargetKeyword, contentAngle, setContentAngle, tone, setTone, seoOptimize, setSeoOptimize }) => {
  const [topicSuggestions, setTopicSuggestions] = useState([]);
  const [keywordSuggestions, setKeywordSuggestions] = useState([]);
  const [brandVoices, setBrandVoices] = useState([]);
  const [showTopicSuggestions, setShowTopicSuggestions] = useState(false);
  const [showKeywordSuggestions, setShowKeywordSuggestions] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    // Fetch topic suggestions
    fetch(`${API_URL}/api/content/suggestions/topics`)
      .then(r => r.json())
      .then(d => setTopicSuggestions(d.topics || []))
      .catch(() => {});
    // Fetch keyword suggestions
    fetch(`${API_URL}/api/content/suggestions/keywords`)
      .then(r => r.json())
      .then(d => setKeywordSuggestions(d.keywords || []))
      .catch(() => {});
    // Fetch brand voices
    if (token) {
      fetch(`${API_URL}/api/content/brand-voices`, { headers: { 'Authorization': `Bearer ${token}` } })
        .then(r => r.json())
        .then(d => setBrandVoices(d.profiles || []))
        .catch(() => {});
    }
  }, []);

  return (
  <div className="space-y-6" data-testid="deep-forge-config">
    {/* Topic */}
    <div className="relative">
      <label className="text-sm font-semibold text-orange-300 mb-2 block">Topic <span className="text-red-400">*</span></label>
      <input
        type="text"
        value={topic}
        onChange={e => setTopic(e.target.value)}
        onFocus={() => setShowTopicSuggestions(true)}
        onBlur={() => setTimeout(() => setShowTopicSuggestions(false), 200)}
        placeholder="What should Hermes forge content about?"
        className="w-full bg-gray-800/50 border border-orange-500/30 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-orange-400/60 focus:outline-none text-lg"
        data-testid="deep-forge-topic"
      />
      {showTopicSuggestions && topicSuggestions.length > 0 && !topic && (
        <div className="absolute z-20 mt-1 w-full bg-gray-900 border border-gray-700 rounded-lg shadow-xl max-h-48 overflow-y-auto">
          <div className="px-3 py-2 text-xs text-gray-500 border-b border-gray-800">Trending topic suggestions</div>
          {topicSuggestions.filter(Boolean).slice(0, 8).map((s, i) => (
            <button key={i} onClick={() => { setTopic(s); setShowTopicSuggestions(false); }} className="w-full px-3 py-2 text-left text-sm text-gray-300 hover:bg-orange-500/10 hover:text-orange-300 transition-colors">
              {s}
            </button>
          ))}
        </div>
      )}
    </div>

    {/* Target Keyword */}
    <div className="relative">
      <label className="text-sm font-semibold text-orange-300 mb-2 block">Target Keyword</label>
      <input
        type="text"
        value={targetKeyword}
        onChange={e => setTargetKeyword(e.target.value)}
        onFocus={() => setShowKeywordSuggestions(true)}
        onBlur={() => setTimeout(() => setShowKeywordSuggestions(false), 200)}
        placeholder="Primary SEO keyword to optimize for"
        className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:border-orange-400/60 focus:outline-none"
        data-testid="deep-forge-keyword"
      />
      {showKeywordSuggestions && keywordSuggestions.length > 0 && !targetKeyword && (
        <div className="absolute z-20 mt-1 w-full bg-gray-900 border border-gray-700 rounded-lg shadow-xl max-h-48 overflow-y-auto">
          <div className="px-3 py-2 text-xs text-gray-500 border-b border-gray-800">SEO keyword suggestions</div>
          {keywordSuggestions.slice(0, 10).map((s, i) => (
            <button key={i} onClick={() => { setTargetKeyword(s); setShowKeywordSuggestions(false); }} className="w-full px-3 py-2 text-left text-sm text-gray-300 hover:bg-orange-500/10 hover:text-orange-300 transition-colors">
              {s}
            </button>
          ))}
        </div>
      )}
    </div>

    {/* Content Angle + Tone */}
    <div className="grid grid-cols-2 gap-4">
      <div>
        <label className="text-sm font-semibold text-orange-300 mb-2 block">Content Angle</label>
        <select
          value={contentAngle}
          onChange={e => setContentAngle(e.target.value)}
          className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-orange-400/60 focus:outline-none"
          data-testid="deep-forge-angle"
        >
          {CONTENT_ANGLES.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
        </select>
      </div>
      <div>
        <label className="text-sm font-semibold text-orange-300 mb-2 block">Tone</label>
        <select
          value={tone}
          onChange={e => setTone(e.target.value)}
          className="w-full bg-gray-800/50 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-orange-400/60 focus:outline-none"
          data-testid="deep-forge-tone"
        >
          {TONES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
          {brandVoices.length > 0 && (
            <optgroup label="Your Brand Voices">
              {brandVoices.map(bv => (
                <option key={bv.id} value={`brand_voice_${bv.id}`}>{bv.label} ({bv.traits.slice(0, 2).join(', ')})</option>
              ))}
            </optgroup>
          )}
        </select>
      </div>
    </div>

    {/* SEO Baked-In Toggle (default ON) */}
    <div className="rounded-xl border border-gray-700 bg-gray-900/40 p-4" data-testid="deep-forge-seo-toggle-section">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span>🔍</span>
            <span className="text-sm font-semibold text-orange-300">SEO Baked Into Generation</span>
          </div>
          <p className="text-xs text-gray-500">
            Auto-generates meta title + description, internal link suggestions, and optimizes for readability (Flesch ≥ 60).
          </p>
        </div>
        <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
          <input
            type="checkbox"
            checked={seoOptimize}
            onChange={(e) => setSeoOptimize(e.target.checked)}
            className="sr-only peer"
            data-testid="deep-forge-seo-toggle"
          />
          <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
        </label>
      </div>
    </div>

    {/* Pipeline Preview */}
    <div className="rounded-xl border border-gray-700/50 bg-gray-900/30 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-700/30 flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-orange-400" />
        <span className="text-sm font-semibold text-gray-300">12-Step Pipeline Preview</span>
      </div>
      <div className="px-4 py-3 grid grid-cols-1 md:grid-cols-2 gap-2 max-h-64 overflow-y-auto">
        {FORGE_STEPS.map(step => (
          <div key={step.num} className="flex items-start gap-2 py-1.5">
            <span className="text-base flex-shrink-0">{step.emoji}</span>
            <div className="min-w-0">
              <span className="text-xs text-gray-500">Step {step.num}:</span>
              <p className="text-xs text-gray-400 truncate">
                {step.desc}<span className="text-orange-400 font-medium">{topic || '[your topic]'}</span>
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Begin Forging Button */}
    <button
      onClick={onStartForge}
      disabled={loading || !topic.trim()}
      className="w-full py-5 rounded-xl font-bold text-lg transition-all bg-gradient-to-r from-orange-600 via-amber-600 to-yellow-600 hover:from-orange-500 hover:via-amber-500 hover:to-yellow-500 text-white disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-3 shadow-lg shadow-orange-500/20"
      data-testid="deep-forge-start-btn"
    >
      {loading ? (
        <><Loader2 className="w-5 h-5 animate-spin" /> Hermes is forging...</>
      ) : (
        <><Flame className="w-5 h-5" /> Begin Forging (25 credits)</>
      )}
    </button>
  </div>
);};

// ─── Deep Forge Progress (SSE-based Live Transparency Accordion) ───
const DeepForgeProgress = ({ jobId, onComplete }) => {
  const [status, setStatus] = useState('running');
  const [steps, setSteps] = useState(() => FORGE_STEPS.map((s) => ({
    step_number: s.num,
    step_name: `${s.emoji} ${s.name}`,
    status: 'pending',
    result_preview: null,
  })));
  const [currentStep, setCurrentStep] = useState(0);
  const [expandedSteps, setExpandedSteps] = useState({});
  const [error, setError] = useState('');

  // Polling fallback (SSE with EventSource can't set auth headers on all hosts)
  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/agents/jobs/${jobId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;

        if (data.progress?.steps) {
          // Merge progress steps keyed by step_number
          const progressSteps = data.progress.steps;
          setSteps(prev => prev.map(p => {
            const ps = progressSteps.find(s => s.step_number === p.step_number);
            return ps ? { ...p, ...ps } : p;
          }));
          setCurrentStep(Math.max(0, (data.progress.current_step || 1) - 1));
        }

        if (data.status === 'completed') {
          setStatus('completed');
          onComplete(data.result);
        } else if (data.status === 'failed') {
          setStatus('failed');
          setError(data.error || 'Forge failed');
        }
      } catch {/* ignore transient errors */}
    };

    const interval = setInterval(() => {
      if (status === 'running') poll();
    }, 2500);
    poll();
    return () => { cancelled = true; clearInterval(interval); };
  }, [jobId, onComplete, status]);

  const toggleStep = (num) => {
    setExpandedSteps(prev => ({ ...prev, [num]: !prev[num] }));
  };

  const completedCount = steps.filter(s => s.status === 'completed').length;

  return (
    <div className="rounded-xl border border-orange-500/30 bg-gray-900/50 p-6" data-testid="deep-forge-progress">
      <div className="flex items-center gap-3 mb-4">
        <Flame className="w-6 h-6 text-orange-400 animate-pulse" />
        <h3 className="font-bold text-white text-lg">Hermes is Forging — Live Transparency</h3>
        <span className="ml-auto text-sm text-orange-400 font-mono">{completedCount}/{steps.length}</span>
      </div>

      {/* Progress Bar */}
      <div className="h-2 bg-gray-800 rounded-full overflow-hidden mb-5">
        <div
          className="h-full bg-gradient-to-r from-orange-500 via-amber-500 to-yellow-400 rounded-full transition-all duration-700"
          style={{ width: `${(completedCount / steps.length) * 100}%` }}
        />
      </div>

      {/* Live Accordion of Steps */}
      <div className="space-y-1.5" data-testid="deep-forge-live-accordion">
        {steps.map((step, i) => {
          const isRunning = step.status === 'running' || (step.status === 'pending' && i === currentStep);
          const isCompleted = step.status === 'completed';
          const isFailed = step.status === 'failed';
          const isExpandable = isCompleted || isFailed;
          const isExpanded = !!expandedSteps[step.step_number];

          return (
            <div
              key={step.step_number}
              className={`rounded-lg border overflow-hidden transition-all ${
                isCompleted ? 'border-green-500/30 bg-green-500/5' :
                isFailed ? 'border-red-500/40 bg-red-500/5' :
                isRunning ? 'border-orange-500/40 bg-orange-500/5' :
                'border-gray-700/40 bg-gray-800/30'
              }`}
              data-testid={`forge-step-${step.step_number}`}
            >
              <button
                type="button"
                onClick={() => isExpandable && toggleStep(step.step_number)}
                disabled={!isExpandable}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left ${isExpandable ? 'cursor-pointer hover:bg-white/[0.02]' : 'cursor-default'}`}
              >
                <span className="text-sm font-bold w-6 flex-shrink-0 text-gray-500">{step.step_number}</span>
                <span className={`flex-1 text-sm font-medium ${
                  isCompleted ? 'text-green-300' :
                  isFailed ? 'text-red-300' :
                  isRunning ? 'text-orange-300' :
                  'text-gray-500'
                }`}>{step.step_name}</span>
                <span className="flex items-center gap-2 flex-shrink-0">
                  {isRunning && <Loader2 className="w-3.5 h-3.5 animate-spin text-orange-400" />}
                  {isCompleted && <span className="text-green-400">✓</span>}
                  {isFailed && <span className="text-red-400">✗</span>}
                  {!isRunning && !isCompleted && !isFailed && <span className="text-gray-600">○</span>}
                  {isExpandable && (
                    <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  )}
                </span>
              </button>

              {/* Expanded step preview */}
              {isExpandable && isExpanded && (
                <div className="px-12 pb-3 pt-1 text-xs text-gray-300 bg-black/20" data-testid={`forge-step-${step.step_number}-output`}>
                  {step.result_preview ? (
                    <div className="whitespace-pre-wrap break-words font-mono text-gray-300">{step.result_preview}</div>
                  ) : step.error_message ? (
                    <div className="text-red-400">{step.error_message}</div>
                  ) : (
                    <div className="text-gray-500 italic">Step completed — no preview available</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {status === 'failed' && (
        <div className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
          {error || 'Forge failed. Credits have been refunded.'}
        </div>
      )}
    </div>
  );
};

// ─── Main Unified Component ───
const UnifiedContentForge = ({ API, user, onRunAgent, onBack }) => {
  const { workflow, startRiver, clearRiver } = useWorkflow();
  const { storeGeneratedContent } = useIntelligence();
  const [mode, setMode] = useState('quick');
  const [quickLoading, setQuickLoading] = useState(false);
  const [quickResult, setQuickResult] = useState(null);
  const [variantsResult, setVariantsResult] = useState(null);
  const [variantMode, setVariantMode] = useState('single');
  const [quickError, setQuickError] = useState('');
  const [quickSeoOptimize, setQuickSeoOptimize] = useState(true);

  // Deep Forge state
  const [deepTopic, setDeepTopic] = useState('');
  const [deepKeyword, setDeepKeyword] = useState('');
  const [deepAngle, setDeepAngle] = useState('thought_leadership');
  const [deepTone, setDeepTone] = useState('professional');
  const [deepSeoOptimize, setDeepSeoOptimize] = useState(true);
  const [deepLoading, setDeepLoading] = useState(false);
  const [deepJobId, setDeepJobId] = useState(null);
  const [deepResult, setDeepResult] = useState(null);
  const [deepError, setDeepError] = useState('');

  // Pre-fill from River workflow (Part C)
  useEffect(() => {
    if (workflow.active && (workflow.targetTool === 'generate' || workflow.targetTool === 'hermes-forge')) {
      // Pre-select Deep Forge when arriving via River (user has specific intent)
      if (workflow.sourceTool && workflow.sourceTool !== '') {
        setMode('deep');
      }
      if (workflow.topic) {
        setDeepTopic(workflow.topic);
      }
      if (workflow.keywords) {
        setDeepKeyword(workflow.keywords.split(',')[0]?.trim() || '');
      }
      if (workflow.competitorGap) {
        setDeepAngle('counter_competitor');
      }
      if (workflow.contentAngle) {
        setDeepAngle(workflow.contentAngle);
      }
    }
  }, [workflow]);

  // Pre-fill from digest email "Forge content on this →" deep-link.
  // App.js writes sessionStorage.isupplyai_pending_forge_topic at module load
  // (before this component mounts). Honor a 1h TTL and consume the key.
  // Sets `prefillTopic` which the Quick form picks up via prop on its mount.
  const [prefillTopic, setPrefillTopic] = useState('');
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('isupplyai_pending_forge_topic');
      if (!raw) return;
      const pending = JSON.parse(raw);
      const ageMs = Date.now() - (pending.ts || 0);
      if (ageMs > 60 * 60 * 1000) {
        sessionStorage.removeItem('isupplyai_pending_forge_topic');
        return;
      }
      if (pending.topic) {
        // Hydrate Deep Forge state immediately (covers Deep mode UX)
        setDeepTopic(pending.topic);
        // Pass to Quick form via state prop
        setPrefillTopic(pending.topic);
        // Switch to Deep Forge if the link asked for it (digest "Forge this brief" button)
        if (pending.mode === 'deep') setMode('deep');
      }
      sessionStorage.removeItem('isupplyai_pending_forge_topic');
    } catch { /* noop */ }
  }, []);

  // Quick Generate handler (single)
  const handleQuickGenerate = async (formData) => {
    setQuickLoading(true);
    setQuickError('');
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        description: formData.description?.trim() || formData.title || 'Generate content on this topic',
      };
      const res = await fetch(`${API_URL}/api/content/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data) {
        setQuickResult(data);
        if (data.title) storeGeneratedContent(data.title);
      } else {
        const detail = data?.detail;
        let msg;
        if (typeof detail === 'string') msg = detail;
        else if (Array.isArray(detail)) msg = detail.map(d => d.msg || d.message || JSON.stringify(d)).join('; ');
        else if (detail && typeof detail === 'object') msg = JSON.stringify(detail);
        else msg = `Generation failed (HTTP ${res.status})`;
        setQuickError(msg);
      }
    } catch (e) {
      setQuickError(`Network error: ${e.message || 'Please check your connection'}`);
    }
    setQuickLoading(false);
  };

  // Quick Generate handler (3 variants)
  const handleQuickVariants = async (formData) => {
    setQuickLoading(true);
    setQuickError('');
    setVariantsResult(null);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        description: formData.description?.trim() || formData.title || 'Generate content on this topic',
      };
      const res = await fetch(`${API_URL}/api/content/quick-variants`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => null);
      if (res.ok && data) {
        setVariantsResult(data);
        if (formData.title) storeGeneratedContent(formData.title);
      } else {
        const detail = data?.detail;
        let msg;
        if (typeof detail === 'string') msg = detail;
        else if (Array.isArray(detail)) msg = detail.map(d => d.msg || d.message || JSON.stringify(d)).join('; ');
        else if (detail && typeof detail === 'object') msg = JSON.stringify(detail);
        else msg = `Variant generation failed (HTTP ${res.status})`;
        setQuickError(msg);
      }
    } catch (e) {
      setQuickError(`Network error: ${e.message || 'Please check your connection'}`);
    }
    setQuickLoading(false);
  };

  // Deep Forge handler
  const handleDeepForge = async () => {
    if (!deepTopic.trim()) return;
    setDeepLoading(true);
    setDeepResult(null);
    setDeepError('');
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/agents/quick-run/content_engine`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          topic: deepTopic,
          content_angle: deepAngle,
          tone: deepTone,
          target_keyword: deepKeyword,
          seo_optimize: deepSeoOptimize,
        })
      });
      if (res.ok) {
        const data = await res.json();
        setDeepJobId(data.job_id);
      } else {
        const err = await res.json().catch(() => ({}));
        const msg = typeof err.detail === 'string' ? err.detail : 'Forge failed. Please try again.';
        setDeepError(msg);
        setDeepLoading(false);
      }
    } catch {
      setDeepError('Forge failed. Please check your connection and try again.');
      setDeepLoading(false);
    }
  };

  const handleDeepComplete = useCallback((result) => {
    setDeepResult(result);
    setDeepLoading(false);
    setDeepJobId(null);
    const title = result?.blog_post?.title || deepTopic;
    if (title) storeGeneratedContent(title);
  }, [deepTopic, storeGeneratedContent]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-gray-950 text-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* River Banner */}
        {workflow.active && (workflow.targetTool === 'generate' || workflow.targetTool === 'hermes-forge') && <RiverBanner />}

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-black bg-gradient-to-r from-blue-400 via-purple-400 to-orange-400 bg-clip-text text-transparent">
            Content Forge
          </h1>
          <p className="text-gray-400 mt-1">Generate content instantly or forge research-backed masterpieces</p>
        </div>

        {/* Mode Toggle */}
        <div className="mb-8">
          <ModeToggle mode={mode} setMode={setMode} />
        </div>

        {/* Quick Generate Mode */}
        {mode === 'quick' && (
          <QuickGenerateForm
            onGenerate={handleQuickGenerate}
            onGenerateVariants={handleQuickVariants}
            loading={quickLoading}
            generatedContent={quickResult}
            variantsResult={variantsResult}
            startRiver={startRiver}
            error={quickError}
            variantMode={variantMode}
            setVariantMode={setVariantMode}
            seoOptimize={quickSeoOptimize}
            setSeoOptimize={setQuickSeoOptimize}
            prefillTopic={prefillTopic}
          />
        )}

        {/* Deep Forge Mode - Config */}
        {mode === 'deep' && !deepJobId && !deepResult && (
          <>
            {deepError && (
              <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-sm">{deepError}</div>
            )}
            <DeepForgeConfig
              onStartForge={handleDeepForge}
              loading={deepLoading}
              topic={deepTopic}
              setTopic={setDeepTopic}
              targetKeyword={deepKeyword}
              setTargetKeyword={setDeepKeyword}
              contentAngle={deepAngle}
              setContentAngle={setDeepAngle}
              tone={deepTone}
              setTone={setDeepTone}
              seoOptimize={deepSeoOptimize}
              setSeoOptimize={setDeepSeoOptimize}
            />
          </>
        )}

        {/* Deep Forge Running */}
        {mode === 'deep' && deepJobId && !deepResult && (
          <DeepForgeProgress jobId={deepJobId} onComplete={handleDeepComplete} />
        )}

        {/* Deep Forge Result */}
        {mode === 'deep' && deepResult && (
          <div className="rounded-xl border border-orange-500/30 bg-gray-900/50 overflow-hidden" data-testid="deep-forge-result">
            <div className="px-6 py-4 border-b border-orange-500/20 bg-gradient-to-r from-orange-500/10 to-amber-500/10 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Flame className="w-5 h-5 text-orange-400" />
                <h3 className="font-bold text-white text-lg">{deepResult.blog_post?.title || deepResult.topic || deepTopic}</h3>
              </div>
              <button onClick={() => { setDeepResult(null); setDeepJobId(null); }} className="text-gray-400 hover:text-white text-sm">New Forge</button>
            </div>
            <div className="px-6 py-4">
              {deepResult.blog_post?.content && (
                <div className="text-sm text-gray-300 leading-relaxed max-h-96 overflow-y-auto whitespace-pre-wrap mb-4">
                  {deepResult.blog_post.content.substring(0, 2000)}{deepResult.blog_post.content.length > 2000 ? '...' : ''}
                </div>
              )}
              <div className="flex flex-wrap gap-3 pt-3 border-t border-gray-700/50">
                <RiverButton label="Distribute This" icon="☀️" onClick={() => startRiver({ topic: deepResult.blog_post?.title || deepTopic, content: deepResult.blog_post?.content || '', targetTool: 'apollo-chamber', sourceTool: 'hermes-forge' })} className="!text-xs !py-2" />
                <RiverButton label="Multiply This" icon="✨" onClick={() => startRiver({ topic: deepResult.blog_post?.title || deepTopic, content: deepResult.blog_post?.content || '', targetTool: 'multiply', sourceTool: 'hermes-forge' })} className="!text-xs !py-2" />
                <RiverButton label="SEO Optimize" icon="🔍" onClick={() => startRiver({ topic: deepResult.blog_post?.title || deepTopic, content: deepResult.blog_post?.content || '', targetTool: 'seo', sourceTool: 'hermes-forge' })} className="!text-xs !py-2" />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UnifiedContentForge;
